import { useFormik } from "formik";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import {
  Alert,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReactPlayer from "react-player/lazy";

import { CreateSkillRequest, SkillDto, State } from "../../interfaces";
import { ActivityButton, SequenceNumberInput } from "../../components/Common";
import {
  addSkill,
  getSkill,
  updateSkill,
  uploadSkillVideo,
} from "../../store/skill/actions";
import { MediaDto } from "../../interfaces/media";
import { VIDEO_ACCEPT } from "../../constants";
import { SequenceEnum } from "../../enums";

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: (event: any) => void;
  lessonId: string;
};
export const AddEditSkillModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
  lessonId,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const [request] = useState<CreateSkillRequest>();
  const { loading, success, error } = useSelector(
    (state: any): State<SkillDto> => state.skill.addSkill
  );

  const {
    loading: updateSkillLoading,
    success: updateSkillSuccess,
    error: updateSkillError,
  } = useSelector((state: any): State<SkillDto> => state.skill.updateSkill);

  const {
    loading: uploadingSkillVideo,
    success: uploadSkillVideoSuccess,
    error: uploadSkillVideoError,
    response: uploadSkillVideoResponse,
  } = useSelector(
    (state: any): State<MediaDto> => state.skill.uploadSkillVideo
  );

  const uploadSkillVideoProgress: number = useSelector(
    (state: any): number => state.skill.uploadSkillVideoProgress
  );

  const { error: getSkillError, response: skill } = useSelector(
    (state: any): State<SkillDto> => state.skill.skill
  );

  //#region form
  const form = useFormik<CreateSkillRequest>({
    enableReinitialize: true,
    initialValues: {
      name: {
        en: request?.name.en || "",
        ar: request?.name.ar || "",
      },
      content: {
        en: request?.content.en || "",
        ar: request?.content.ar || "",
      },
      videoId: request?.videoId,
      lessonId: lessonId,
      sequenceNumber: request?.sequenceNumber || 0,
    },
    initialErrors: error || updateSkillError,
    validationSchema: Yup.object({
      name: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" })
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            })
          ),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" })
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            })
          ),
      }),
      content: Yup.object({
        en: Yup.string().required(
          t("isNotEmpty", {
            displayName: "englishValue",
          })
        ),
        ar: Yup.string().required(
          t("isNotEmpty", {
            displayName: "arabicValue",
          })
        ),
      }),
      lessonId: Yup.string(),
      videoId: Yup.string(),
      sequenceNumber: Yup.number().required(
        t("isNotEmpty", {
          displayName: "sequenceNumber",
        })
      ),
    }),
    onSubmit: (values) => {
      if (id) {
        dispatch(
          updateSkill(
            id,
            { ...values, sequenceNumber: Number(values.sequenceNumber) },
            history
          )
        );
      } else {
        dispatch(
          addSkill(
            { ...values, sequenceNumber: Number(values.sequenceNumber) },
            history
          )
        );
      }
    },
  });
  //#endregion

  //#region effects
  useEffect(() => {
    if ((!loading && success) || (!updateSkillLoading && updateSkillSuccess)) {
      onClose();
    }
  }, [loading, updateSkillLoading]);

  //#endregion

  //#region handlers
  const onClose = () => {
    form.resetForm();
    onCloseClick(!show);
  };

  const onChangeSkillVideo = (event: ChangeEvent<HTMLInputElement>) => {
    const { target } = event;
    const { files } = target;
    if (files && files.length > 0) {
      const imageFile = files[0];
      if (
        !imageFile.name.match(/\.(mp4|avi|mkv|mov|wmv|flv|3gp|ogg|mpeg|mpg)$/)
      ) {
        toast.error(t("selectValidVideo"), {
          autoClose: 3000,
        });
        return false;
      }
      dispatch(uploadSkillVideo(imageFile, history, skill?.id));
    }
  };

  const getUploadingStatus = () => {
    if (uploadingSkillVideo) {
      return (
        <React.Fragment>
          {t("uploadProgress", { progress: uploadSkillVideoProgress })}{" "}
          <Spinner size={"sm"} />
        </React.Fragment>
      );
    } else {
      return (
        <React.Fragment>
          {t("uploadVideo")}{" "}
          <i className="ri-video-upload-line me-1 align-bottom"></i>
        </React.Fragment>
      );
    }
  };

  //#endregion

  //#region effects

  useEffect(() => {
    if (id) {
      dispatch(getSkill(id, history));
    }
  }, [id]);

  useEffect(() => {
    if (id && skill) {
      form.setValues({
        name: skill.name,
        content: skill.content,
        videoId: skill.video?.id,
        lessonId: lessonId,
        sequenceNumber:
          form.values.sequenceNumber || skill?.sequenceNumber || 0,
      });
    }
  }, [skill]);

  useEffect(() => {
    if (
      !uploadingSkillVideo &&
      uploadSkillVideoSuccess &&
      uploadSkillVideoResponse
    ) {
      form.setFieldValue("videoId", uploadSkillVideoResponse.id);
    }
  }, [uploadingSkillVideo]);

  //#endregion

  return (
    <React.Fragment>
      <ToastContainer />
      <Modal
        id="addEdit"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        centered
        className="modal-lg"
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalHeader
            className="p-3"
            toggle={() => {
              onClose();
            }}
          >
            {id
              ? t("editItem", { item: "skill" })
              : t("addItem", { item: "skill" })}
          </ModalHeader>
          {typeof uploadSkillVideoError === "string" && (
            <Alert color="danger" className="rounded-0 mb-0">
              <p className="mb-0">{uploadSkillVideoError}</p>
            </Alert>
          )}
          {typeof error === "string" && (
            <Alert color="danger" className="rounded-0 mb-0">
              <p className="mb-0">{error}</p>
            </Alert>
          )}
          {typeof getSkillError === "string" && (
            <Alert color="danger" className="rounded-0 mb-0">
              <p className="mb-0">{getSkillError}</p>
            </Alert>
          )}
          {typeof updateSkillError === "string" && (
            <Alert color="danger" className="rounded-0 mb-0">
              <p className="mb-0">{updateSkillError}</p>
            </Alert>
          )}
          <ModalBody>
            <Row className="align-items-center justify-content-center">
              <Col className="text-center">
                {skill?.video && skill.video.path && (
                  <div className="player-wrapper">
                    <ReactPlayer
                      className="react-player"
                      url={skill.video.path}
                      width="100%"
                      height="100%"
                      controls={true}
                    />
                  </div>
                )}
                <Label
                  className="btn btn-success mt-1 cursor-pointer"
                  htmlFor="skill-video"
                >
                  {getUploadingStatus()}
                </Label>
                <input
                  disabled={uploadingSkillVideo}
                  type="file"
                  className="d-none"
                  id="skill-video"
                  accept={VIDEO_ACCEPT}
                  onChange={onChangeSkillVideo}
                />
              </Col>
            </Row>
            <SequenceNumberInput
              tabIndex={1}
              value={form.values.sequenceNumber}
              onChange={(e) => {
                e.preventDefault();
                const { value } = e.target;
                const regex =
                  /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                if (regex.test(value.toString())) {
                  form.setFieldValue("sequenceNumber", value);
                } else {
                  form.setFieldValue("sequenceNumber", "");
                }
              }}
              onBlur={form.handleBlur}
              invalid={
                form.touched.sequenceNumber && form.errors.sequenceNumber
                  ? true
                  : false
              }
              sequenceType={SequenceEnum.SKILL}
              onGenerateSequenceNumber={(v: number) => {
                form.setFieldValue("sequenceNumber", v);
              }}
              mode={id ? "edit" : "add"}
              request={{
                type: SequenceEnum.SKILL,
                lessonId: lessonId,
              }}
            />
            {form.touched.sequenceNumber && form.errors.sequenceNumber ? (
              <FormFeedback type="invalid">
                {form.errors.sequenceNumber as string}
              </FormFeedback>
            ) : null}
            <div className="mb-3">
              <label htmlFor="name.en" className="form-label">
                {t("nameEn")}
                <span className="text-danger">*</span>
              </label>
              <Input
                autoComplete="off"
                id="name.en"
                name="name.en"
                value={form.values?.name.en}
                type="text"
                className="form-control"
                placeholder={t("enterInEnglish")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.name?.en && form.errors.name?.en ? true : false
                }
              />
              {form.touched.name?.en && form.errors.name?.en ? (
                <FormFeedback type="invalid">
                  {form.errors.name?.en as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="name.ar" className="form-label">
                {t("nameAr")}
                <span className="text-danger">*</span>
              </label>
              <Input
                style={{ fontFamily: "aljannat", direction: "rtl" }}
                autoComplete="off"
                auto
                id="name.ar"
                name="name.ar"
                value={form.values.name?.ar}
                type="text"
                className="form-control"
                placeholder={t("enterInArabic")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.name?.ar && form.errors.name?.ar ? true : false
                }
              />
              {form.touched.name?.ar && form.errors.name?.ar ? (
                <FormFeedback type="invalid">
                  {form.errors.name?.ar as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="content.en" className="form-label">
                {t("contentEn")}
                <span className="text-danger">*</span>
              </label>
              <Input
                style={{ resize: "none" }}
                rows={10}
                autoComplete="off"
                id="content.en"
                name="content.en"
                value={form.values?.content.en}
                type="textarea"
                className="form-control"
                placeholder={t("enterInEnglish")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.content?.en && form.errors.content?.en
                    ? true
                    : false
                }
              />
              {form.touched.content?.en && form.errors.content?.en ? (
                <FormFeedback type="invalid">
                  {form.errors.content?.en as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="content.ar" className="form-label">
                {t("contentAr")}
                <span className="text-danger">*</span>
              </label>
              <Input
                style={{ resize: "none", fontFamily: "aljannat" }}
                rows={10}
                autoComplete="off"
                id="content.ar"
                name="content.ar"
                value={form.values?.content.ar}
                type="textarea"
                className="form-control"
                placeholder={t("enterInArabic")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.content?.ar && form.errors.content?.ar
                    ? true
                    : false
                }
              />
              {form.touched.content?.ar && form.errors.content?.ar ? (
                <FormFeedback type="invalid">
                  {form.errors.content?.ar as string}
                </FormFeedback>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter>
            <Link
              to={"#"}
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            <ActivityButton
              title="save"
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="submit"
              inProgress={loading || updateSkillLoading}
              disabled={
                loading ||
                updateSkillLoading ||
                uploadingSkillVideo ||
                getSkillError
              }
            ></ActivityButton>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};
